import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    IconButton,
    TextField,
    Divider,
    Paper,
    Stack,
    MenuItem,
    InputAdornment,
    Tooltip,
    Chip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    AddCircle as AddCircleIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIndicatorIcon,
    Search as SearchIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';

const FIELD_TYPES = ['text', 'number', 'date', 'select', 'textarea', 'address', 'header', 'file'];

// localStorage helpers (per project)
const recentKey = (projectsid) => `form_recent_fields_${projectsid}`;

function loadRecentFields(projectsid) {
    try {
        const raw = localStorage.getItem(recentKey(projectsid));
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveRecentFields(projectsid, items) {
    try {
        localStorage.setItem(recentKey(projectsid), JSON.stringify(items.slice(0, 25)));
    } catch {
        // ignore
    }
}

const FormDetails = ({
                         form,
                         isEditing,
                         setIsEditing,
                         onSave,
                         onEdit,
                         onCancel,
                         onUpdateForm,
                         onCreateField,
                         availableFields = [],
                     }) => {
    const [fields, setFields] = useState(form.fields || []);
    const [formTitle, setFormTitle] = useState(form.form_title || '');
    const [libraryOpen, setLibraryOpen] = useState(true);
    const [search, setSearch] = useState('');

    const [recentFields, setRecentFields] = useState(() =>
        form?.projectsid ? loadRecentFields(form.projectsid) : []
    );

    // Keep in sync when switching forms
    useEffect(() => {
        setFields(form.fields || []);
        setFormTitle(form.form_title || '');
        setSearch('');
        if (form?.projectsid) {
            setRecentFields(loadRecentFields(form.projectsid));
        } else {
            setRecentFields([]);
        }
    }, [form]);

    // Helper: push to recent history
    const pushRecent = (field) => {
        if (!form?.projectsid) return;

        // normalize minimal shape for recent list
        const item = {
            field_id: field.field_id,
            field_name: field.field_name,
            field_type: field.field_type,
            projectsid: form.projectsid,
        };

        const next = [
            item,
            ...recentFields.filter((f) => f.field_id !== item.field_id),
        ].slice(0, 25);

        setRecentFields(next);
        saveRecentFields(form.projectsid, next);
    };

    const syncUp = (newFields, newTitle = formTitle) => {
        setFields(newFields);
        onUpdateForm({ ...form, form_title: newTitle, fields: newFields });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            const oldIndex = fields.findIndex((f) => f.field_id === active.id);
            const newIndex = fields.findIndex((f) => f.field_id === over.id);
            const newFields = arrayMove(fields, oldIndex, newIndex);
            syncUp(newFields);
        }
    };

    const handleTitleChange = (event) => {
        const nextTitle = event.target.value;
        setFormTitle(nextTitle);
        onUpdateForm({ ...form, form_title: nextTitle, fields });
    };

    const handleDeleteField = (fieldId) => {
        // BUG-001 part 3 (client): defense-in-depth. The Delete button is
        // already disabled on protected fields in <SortableField>, but if a
        // future refactor wires up a different remove path (bulk action,
        // keyboard shortcut, etc.) the FE should still refuse to drop a
        // protected field on its own — otherwise saveForm would round-trip
        // to a 403 from FormHandler.php's saveForm guard.
        const target = fields.find((f) => f.field_id === fieldId);
        if (target?.protected) {
            return;
        }
        const next = fields.filter((f) => f.field_id !== fieldId);
        syncUp(next);
    };

    const handleEditField = (fieldId, patch) => {
        // BUG-001 part 3 (client): same as above. Renaming or retyping a
        // protected field would 403 on the FormHandler.php updateField guard
        // anyway; refusing locally keeps the form state in sync with what the
        // server will accept and avoids a "save"-then-error UX dead-end.
        const target = fields.find((f) => f.field_id === fieldId);
        if (target?.protected && (
            (typeof patch.field_name === 'string' && patch.field_name !== target.field_name) ||
            (typeof patch.field_type === 'string' && patch.field_type !== target.field_type)
        )) {
            return;
        }
        const next = fields.map((f) => (f.field_id === fieldId ? { ...f, ...patch } : f));
        syncUp(next);
    };

    const alreadyOnForm = useMemo(() => new Set(fields.map((f) => f.field_id)), [fields]);

    const filteredAvailable = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = availableFields || [];
        return base
            .filter((f) => !alreadyOnForm.has(f.field_id))
            .filter((f) => {
                if (!q) return true;
                const name = (f.field_name || '').toLowerCase();
                const type = (f.field_type || '').toLowerCase();
                return name.includes(q) || type.includes(q);
            });
    }, [availableFields, alreadyOnForm, search]);

    const filteredRecent = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (recentFields || [])
            .filter((f) => !alreadyOnForm.has(f.field_id))
            .filter((f) => {
                if (!q) return true;
                const name = (f.field_name || '').toLowerCase();
                const type = (f.field_type || '').toLowerCase();
                return name.includes(q) || type.includes(q);
            });
    }, [recentFields, alreadyOnForm, search]);

    const handleAddExistingField = (field) => {
        const next = [...fields, field];
        syncUp(next);
        pushRecent(field);
    };

    // “Create on the fly” (client-side only). If you want it persisted, call your API here.
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldType, setNewFieldType] = useState('text');

    useEffect(() => {
        setNewFieldName('');
        setNewFieldType('text');
    }, [form?.id]);

    const handleCreateField = async () => {
        const name = newFieldName.trim();
        if (!name) return;

        if (!onCreateField) {
            console.error('onCreateField prop not provided');
            return;
        }

        await onCreateField(name, newFieldType);

        setNewFieldName('');
        setNewFieldType('text');
    };


    return (
        <Box flex={1} p={2} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Main column */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Form Title"
                        value={formTitle}
                        onChange={handleTitleChange}
                        sx={{ mb: 2 }}
                    />
                ) : (
                    <Typography variant="h5" sx={{ mb: 1 }}>
                        {formTitle}
                    </Typography>
                )}

                <Typography variant="subtitle1" sx={{ mb: 2, opacity: 0.8 }}>
                    Project: {form.project_name}
                </Typography>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1">Fields</Typography>
                        {isEditing ? (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={onSave}
                                    size="small"
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={onCancel}
                                    size="small"
                                >
                                    Cancel
                                </Button>
                            </Stack>
                        ) : (
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={onEdit}
                                size="small"
                            >
                                Edit
                            </Button>
                        )}
                    </Stack>

                    <Divider sx={{ mb: 1 }} />

                    {isEditing ? (
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={fields.map((f) => f.field_id)}>
                                <List sx={{ p: 0 }}>
                                    {fields.map((field) => (
                                        <SortableField
                                            key={field.field_id}
                                            field={field}
                                            onDelete={() => handleDeleteField(field.field_id)}
                                            onChange={(patch) => handleEditField(field.field_id, patch)}
                                        />
                                    ))}
                                </List>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {fields.map((field) => (
                                <Box
                                    key={field.field_id}
                                    sx={{
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Typography sx={{ flex: 1, minWidth: 0 }}>
                                        {field.field_name} <span style={{ opacity: 0.7 }}>({field.field_type})</span>
                                    </Typography>
                                    {field.protected && (
                                        <Tooltip title="This field is required by every project's submission flow and cannot be removed or renamed.">
                                            <Chip size="small" label="Required" color="primary" variant="outlined" />
                                        </Tooltip>
                                    )}
                                </Box>
                            ))}
                        </List>
                    )}

                    {isEditing && (
                        <Box sx={{ mt: 1 }}>
                            <Button
                                variant="text"
                                startIcon={libraryOpen ? <CloseIcon /> : <AddCircleIcon />}
                                onClick={() => setLibraryOpen((v) => !v)}
                                size="small"
                            >
                                {libraryOpen ? 'Hide field library' : 'Add field'}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Inline Field Library (no dialog) */}
            {isEditing && libraryOpen && (
                <Paper
                    variant="outlined"
                    sx={{
                        width: 360,
                        flexShrink: 0,
                        p: 1.5,
                        position: 'sticky',
                        top: 16,
                        maxHeight: 'calc(100vh - 32px)',
                        overflowY: 'auto',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Field Library
                    </Typography>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search fields…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5 }}
                    />

                    {/* Create new field */}
                    <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.8 }}>
                        Create new
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Name"
                            value={newFieldName}
                            onChange={(e) => setNewFieldName(e.target.value)}
                        />
                        <TextField
                            size="small"
                            select
                            label="Type"
                            value={newFieldType}
                            onChange={(e) => setNewFieldType(e.target.value)}
                            sx={{ width: 140 }}
                        >
                            {FIELD_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<AddCircleIcon />}
                        onClick={handleCreateField}
                        disabled={!newFieldName.trim()}
                        sx={{ mb: 1.5 }}
                    >
                        Add new field
                    </Button>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Recent fields */}
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                        Recent
                    </Typography>

                    <Stack spacing={1} sx={{ mb: 2 }}>
                        {filteredRecent.length === 0 ? (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                No recent fields (or all are already on this form).
                            </Typography>
                        ) : (
                            filteredRecent.map((f) => (
                                <LibraryRow
                                    key={`recent_${f.field_id}`}
                                    field={f}
                                    onAdd={() => handleAddExistingField(f)}
                                    tag="recent"
                                />
                            ))
                        )}
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Existing project fields */}
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                        Existing fields
                    </Typography>

                    <Stack spacing={1}>
                        {filteredAvailable.length === 0 ? (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                No available fields (or all are already on this form).
                            </Typography>
                        ) : (
                            filteredAvailable.map((f) => (
                                <LibraryRow
                                    key={`avail_${f.field_id}`}
                                    field={f}
                                    onAdd={() => handleAddExistingField(f)}
                                />
                            ))
                        )}
                    </Stack>
                </Paper>
            )}
        </Box>
    );
};

const LibraryRow = ({ field, onAdd, tag }) => {
    return (
        <Box
            sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                    {field.field_name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {field.field_type}
                </Typography>
            </Box>

            {tag && <Chip size="small" label={tag} />}

            <Button variant="outlined" size="small" onClick={onAdd}>
                Add
            </Button>
        </Box>
    );
};

const SortableField = ({ field, onDelete, onChange }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
    } = useSortable({ id: field.field_id });

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
    };

    // BUG-001 part 3 (client): lock the rename / retype / delete controls for
    // fields the server (FormHandler.php) considers protected. Without this
    // the admin can edit the value here, hit Save, and only THEN learn the
    // field couldn't be touched (we'd round-trip to a 403 from updateField or
    // saveForm). Reading the flag from `field.protected` (annotated by
    // annotateProtectedFields() in PHP) keeps the protected-name list in one
    // place — the FE doesn't have to mirror PROTECTED_FIELD_NAMES.
    const isProtected = Boolean(field.protected);
    const protectedTooltip = "This field is required by every project's submission flow and cannot be renamed, retyped, or removed.";

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                mb: 1,
                p: 1,
                border: '1px solid',
                borderColor: isProtected ? 'primary.light' : 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: isProtected ? 'action.hover' : 'transparent',
            }}
        >
            {/* Drag handle */}
            <Tooltip title="Drag to reorder">
                <IconButton
                    size="small"
                    ref={setActivatorNodeRef}
                    {...listeners}
                    {...attributes}
                    sx={{ cursor: 'grab' }}
                >
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Inline editable name/type */}
            <Tooltip title={isProtected ? protectedTooltip : ''} disableHoverListener={!isProtected}>
                <TextField
                    size="small"
                    label="Name"
                    value={field.field_name || ''}
                    onChange={(e) => onChange({ field_name: e.target.value })}
                    disabled={isProtected}
                    sx={{ flex: 1 }}
                />
            </Tooltip>

            <Tooltip title={isProtected ? protectedTooltip : ''} disableHoverListener={!isProtected}>
                <TextField
                    size="small"
                    select
                    label="Type"
                    value={field.field_type || 'text'}
                    onChange={(e) => onChange({ field_type: e.target.value })}
                    disabled={isProtected}
                    sx={{ width: 140 }}
                >
                    {FIELD_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                            {t}
                        </MenuItem>
                    ))}
                </TextField>
            </Tooltip>

            {isProtected && (
                <Tooltip title={protectedTooltip}>
                    <Chip size="small" label="Required" color="primary" variant="outlined" />
                </Tooltip>
            )}

            {/* Delete */}
            <Tooltip title={isProtected ? protectedTooltip : 'Remove field from form'}>
                {/* span wrapper so MUI Tooltip still fires on disabled IconButton */}
                <span>
                    <IconButton
                        size="small"
                        onClick={onDelete}
                        color="error"
                        disabled={isProtected}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

export default FormDetails;
